import { useCallback, useEffect, useMemo, useState } from "react";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { Inventory } from "../../../../../api";
import { usePermissions } from "../../../../../hooks/usePermissions";
import { ACTIONS } from "../../../../../utils/constants";

import styles from "./InventoryAdjustmentPage.module.scss";
import {
  INITIAL_VALUES,
  createAdjustmentItem,
  flattenProducts,
  formatCurrency,
  formatNumber,
  validationSchema,
} from "./InventoryAdjustmentPage.form";

function getExistencesResponse(result) {
  const response =
    result?.existences ||
    result?.data?.existences ||
    result?.response?.data?.existences ||
    {};

  if (Array.isArray(response?.docs)) {
    return response;
  }

  if (Array.isArray(response)) {
    return {
      docs: response,
      totalDocs: response.length,
    };
  }

  return {
    docs: [],
    totalDocs: 0,
  };
}

function getErrorMessage(error) {
  if (!error) {
    return "No fue posible completar la operación.";
  }

  if (typeof error === "string") {
    return error;
  }

  return error.msg || error.message || "No fue posible completar la operación.";
}

function getItemError(errors, index, fieldName) {
  const itemErrors = errors?.items;

  if (!Array.isArray(itemErrors)) {
    return "";
  }

  const rowError = itemErrors[index];

  if (typeof rowError === "string") {
    return rowError;
  }

  return rowError?.[fieldName] || "";
}

function LocationVariantAutoAdder({
  selectedVariant,
  items,
  setFieldValue,
}) {
  useEffect(() => {
    if (!selectedVariant) {
      return;
    }

    const alreadyAdded = items.some(
      (item) =>
        String(item.product) === String(selectedVariant.productId) &&
        String(item.variantId) === String(selectedVariant.variantId)
    );

    if (!alreadyAdded) {
      setFieldValue("items", [
        ...items,
        createAdjustmentItem(selectedVariant),
      ]);
    }
  }, [items, selectedVariant, setFieldValue]);

  return null;
}

export function InventoryAdjustmentPage() {
  const location = useLocation();
  const navigate = useNavigate();

  const { hasAction } = usePermissions();
  // Ajuste de inventario es un permiso propio y específico (ADJUST), no el
  // genérico "ADD" -- el botón de la lista ya se oculta sin este permiso,
  // pero esta página se puede alcanzar por URL directa, así que el
  // envío del formulario también se bloquea acá.
  const canAdjust = hasAction("inventory", ACTIONS.ADJUST);

  const [searchValue, setSearchValue] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [availableVariants, setAvailableVariants] = useState([]);

  const [isLoadingVariants, setIsLoadingVariants] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [serverError, setServerError] = useState("");

  const initialSelectedProduct = location.state?.productId || "";
  const initialSelectedVariant = location.state?.variantId || "";

  const loadVariants = useCallback(async () => {
    try {
      setIsLoadingVariants(true);
      setServerError("");

      const inventoryController = new Inventory();

      const result = await inventoryController.getExistences(
        1,
        100,
        {
          search: appliedSearch || undefined,
        }
      );

      const response = getExistencesResponse(result);

      setAvailableVariants(flattenProducts(response.docs || []));
    } catch (errorResponse) {
      console.error("load adjustment variants error:", errorResponse);

      setAvailableVariants([]);
      setServerError(
        getErrorMessage(errorResponse) ||
          "No fue posible cargar productos y variantes."
      );
    } finally {
      setIsLoadingVariants(false);
    }
  }, [appliedSearch]);

  useEffect(() => {
    loadVariants();
  }, [loadVariants]);

  const selectedLocationVariant = useMemo(() => {
    if (!initialSelectedProduct || !initialSelectedVariant) {
      return null;
    }

    return availableVariants.find(
      (variant) =>
        String(variant.productId) === String(initialSelectedProduct) &&
        String(variant.variantId) === String(initialSelectedVariant)
    );
  }, [
    availableVariants,
    initialSelectedProduct,
    initialSelectedVariant,
  ]);

  function handleSearchSubmit() {
    setAppliedSearch(searchValue.trim());
  }

  function handleSearchInputKeyDown(event) {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSearchSubmit();
    }
  }

  function handleCancel() {
    navigate("/admin/inventory");
  }

  async function handleSaveDraft(values) {
    try {
      setIsSavingDraft(true);

      window.localStorage.setItem(
        "inventory-adjustment-draft",
        JSON.stringify({
          adjustmentType: values.adjustmentType,
          reference: values.reference,
          notes: values.notes,
          items: values.items,
          savedAt: new Date().toISOString(),
        })
      );

      setServerError("");
    } catch (error) {
      console.error("save inventory adjustment draft error:", error);

      setServerError(
        "No fue posible guardar el borrador localmente."
      );
    } finally {
      setIsSavingDraft(false);
    }
  }

  async function handleSubmit(values, { setSubmitting, setErrors }) {
    try {
      setServerError("");

      const isPositiveAdjustment = values.adjustmentType === "IN";

      const payload = {
        adjustmentType: values.adjustmentType,
        reference: String(values.reference || "").trim(),
        notes: String(values.notes || "").trim(),
        items: values.items.map((item) => ({
          product: String(item.product || "").trim(),
          variantId: String(item.variantId || "").trim(),
          quantity: Number(item.quantity),
          unitCost: isPositiveAdjustment
            ? item.unitCost === "" ||
              item.unitCost === null ||
              item.unitCost === undefined
              ? null
              : Number(item.unitCost)
            : null,
        })),
      };

      const inventoryController = new Inventory();

      await inventoryController.createAdjustment(payload);

      navigate("/admin/inventory", {
        state: {
          success: "Ajuste de inventario registrado correctamente.",
        },
      });
    } catch (errorResponse) {
      console.error("createAdjustment error:", errorResponse);

      const responseData =
        errorResponse?.response?.data ||
        errorResponse?.data ||
        errorResponse ||
        {};

      if (responseData?.errors) {
        setErrors(responseData.errors);
      }

      setServerError(
        responseData?.msg ||
          responseData?.message ||
          "No fue posible registrar el ajuste de inventario."
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className={styles.InventoryAdjustmentPage}>
      <Formik
        initialValues={INITIAL_VALUES}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          setFieldValue,
          isSubmitting,
        }) => {
          const isPositiveAdjustment =
            values.adjustmentType === "IN";

          const totalItems = values.items.length;

          const totalQuantity = values.items.reduce(
            (total, item) => total + (Number(item.quantity) || 0),
            0
          );

          const totalValue = values.items.reduce(
            (total, item) =>
              total +
              (Number(item.quantity) || 0) *
                (Number(item.unitCost) || 0),
            0
          );

          function handleAdjustmentTypeChange(adjustmentType) {
            setFieldValue("adjustmentType", adjustmentType);
            setServerError("");
          }

          function handleAddVariant(variant) {
            const alreadyAdded = values.items.some(
              (item) =>
                String(item.product) === String(variant.productId) &&
                String(item.variantId) === String(variant.variantId)
            );

            if (alreadyAdded) {
              setServerError(
                "Esta variante ya fue agregada al detalle del ajuste."
              );

              return;
            }

            if (
              values.adjustmentType === "OUT" &&
              Number(variant.availableStock) <= 0
            ) {
              setServerError(
                "No puedes realizar un ajuste negativo sobre una variante sin existencias disponibles."
              );

              return;
            }

            setFieldValue("items", [
              ...values.items,
              createAdjustmentItem(variant),
            ]);

            setServerError("");
          }

          function handleRemoveItem(localId) {
            setFieldValue(
              "items",
              values.items.filter((item) => item.localId !== localId)
            );
          }

          return (
            <>
              <LocationVariantAutoAdder
                selectedVariant={selectedLocationVariant}
                items={values.items}
                setFieldValue={setFieldValue}
              />

              <header className={styles.PageHeader}>
                <div className={styles.TitleSection}>
                  <Link
                    to="/admin/inventory"
                    className={styles.BackButton}
                    aria-label="Volver a existencias"
                  >
                    ←
                  </Link>

                  <div>
                    <p className={styles.Breadcrumb}>
                      Inventario / Ajuste de inventario
                    </p>

                    <h1>Ajuste de inventario</h1>

                    <p className={styles.Subtitle}>
                      Corrige diferencias entre las existencias físicas y el
                      stock registrado en el sistema.
                    </p>
                  </div>
                </div>

                <div className={styles.HeaderActions}>
                  <button
                    type="button"
                    className={styles.CancelButton}
                    onClick={handleCancel}
                    disabled={isSubmitting}
                  >
                    Cancelar
                  </button>

                  <button
                    type="button"
                    className={styles.DraftButton}
                    onClick={() => handleSaveDraft(values)}
                    disabled={isSubmitting || isSavingDraft}
                  >
                    {isSavingDraft
                      ? "Guardando..."
                      : "Guardar borrador"}
                  </button>

                  <button
                    type="submit"
                    form="inventory-adjustment-form"
                    className={
                      isPositiveAdjustment
                        ? styles.ConfirmPositiveButton
                        : styles.ConfirmNegativeButton
                    }
                    disabled={isSubmitting || !canAdjust}
                  >
                    {isSubmitting
                      ? "Confirmando..."
                      : "Confirmar ajuste"}
                  </button>
                </div>
              </header>

              {!canAdjust && (
                <div className={styles.ValidationMessage} role="alert">
                  <span className={styles.MessageIcon} aria-hidden="true">
                    !
                  </span>

                  <span>
                    No tienes permiso para registrar ajustes de inventario.
                  </span>
                </div>
              )}

              {serverError && (
                <div className={styles.ServerErrorMessage} role="alert">
                  <span className={styles.MessageIcon} aria-hidden="true">
                    !
                  </span>

                  <span>{serverError}</span>
                </div>
              )}

              {Object.keys(errors).length > 0 && (
                <div className={styles.ValidationMessage} role="alert">
                  <span className={styles.MessageIcon} aria-hidden="true">
                    !
                  </span>

                  <div>
                    <strong>Revisa los datos del ajuste.</strong>

                    <span>
                      Corrige los campos marcados antes de confirmar el
                      movimiento.
                    </span>
                  </div>
                </div>
              )}

              <Form
                id="inventory-adjustment-form"
                className={styles.FormContent}
              >
                <section className={styles.AdjustmentTypeCard}>
                  <header className={styles.SectionHeader}>
                    <div>
                      <h2>Tipo de ajuste</h2>

                      <p>
                        Define si debes aumentar o disminuir las existencias.
                      </p>
                    </div>
                  </header>

                  <div className={styles.AdjustmentTypeContent}>
                    <button
                      type="button"
                      className={
                        isPositiveAdjustment
                          ? `${styles.AdjustmentTypeButton} ${styles.activePositive}`
                          : styles.AdjustmentTypeButton
                      }
                      onClick={() => handleAdjustmentTypeChange("IN")}
                      disabled={isSubmitting}
                    >
                      <span className={styles.TypeSymbol}>+</span>

                      <span>
                        <strong>Ajuste positivo</strong>

                        <small>
                          Aumenta las existencias por sobrante o corrección.
                        </small>
                      </span>
                    </button>

                    <button
                      type="button"
                      className={
                        !isPositiveAdjustment
                          ? `${styles.AdjustmentTypeButton} ${styles.activeNegative}`
                          : styles.AdjustmentTypeButton
                      }
                      onClick={() => handleAdjustmentTypeChange("OUT")}
                      disabled={isSubmitting}
                    >
                      <span className={styles.TypeSymbol}>−</span>

                      <span>
                        <strong>Ajuste negativo</strong>

                        <small>
                          Reduce existencias por daño, pérdida o corrección.
                        </small>
                      </span>
                    </button>
                  </div>

                  <ErrorMessage
                    name="adjustmentType"
                    component="span"
                    className={styles.AdjustmentTypeError}
                  />
                </section>

                <div className={styles.FormColumns}>
                  <section className={styles.GeneralInformationCard}>
                    <header className={styles.SectionHeader}>
                      <div>
                        <h2>Información general</h2>

                        <p>
                          La nota es obligatoria para justificar el ajuste.
                        </p>
                      </div>
                    </header>

                    <div className={styles.GeneralInformationContent}>
                      <div className={styles.FormField}>
                        <label htmlFor="reference">
                          Referencia / Documento
                        </label>

                        <Field
                          id="reference"
                          name="reference"
                          type="text"
                          placeholder="Ej. AJU-2025-000154"
                          disabled={isSubmitting}
                        />

                        <ErrorMessage
                          name="reference"
                          component="span"
                          className={styles.FieldError}
                        />
                      </div>

                      <div className={styles.FormField}>
                        <label htmlFor="notes">
                          Motivo del ajuste
                        </label>

                        <Field
                          as="textarea"
                          id="notes"
                          name="notes"
                          placeholder={
                            isPositiveAdjustment
                              ? "Ej. Sobrante detectado durante conteo físico."
                              : "Ej. Merma o producto dañado detectado durante conteo físico."
                          }
                          maxLength="1000"
                          disabled={isSubmitting}
                        />

                        <div className={styles.TextAreaFooter}>
                          <ErrorMessage
                            name="notes"
                            component="span"
                            className={styles.FieldError}
                          />

                          <span>{values.notes.length} / 1000</span>
                        </div>
                      </div>
                    </div>
                  </section>

                  <section className={styles.ProductSearchCard}>
                    <header className={styles.SectionHeader}>
                      <div>
                        <h2>Buscar producto / variante</h2>

                        <p>
                          Busca por nombre, SKU o código para agregar al
                          ajuste.
                        </p>
                      </div>
                    </header>

                    <div className={styles.ProductSearchContent}>
                      <div className={styles.SearchForm}>
                        <input
                          type="search"
                          value={searchValue}
                          onChange={(event) =>
                            setSearchValue(event.target.value)
                          }
                          onKeyDown={handleSearchInputKeyDown}
                          placeholder="Buscar producto, SKU o código de barras..."
                          aria-label="Buscar producto o variante"
                          disabled={isLoadingVariants || isSubmitting}
                        />

                        <button
                          type="button"
                          onClick={handleSearchSubmit}
                          aria-label="Buscar"
                          disabled={isLoadingVariants || isSubmitting}
                        >
                          ⌕
                        </button>
                      </div>

                      {isLoadingVariants ? (
                        <div className={styles.SearchState}>
                          Cargando variantes...
                        </div>
                      ) : availableVariants.length === 0 ? (
                        <div className={styles.SearchState}>
                          No se encontraron productos o variantes.
                        </div>
                      ) : (
                        <div className={styles.SearchResults}>
                          {availableVariants.map((variant) => {
                            const alreadyAdded = values.items.some(
                              (item) =>
                                String(item.product) ===
                                  String(variant.productId) &&
                                String(item.variantId) ===
                                  String(variant.variantId)
                            );

                            const hasAvailableStock =
                              Number(variant.availableStock) > 0;

                            const isDisabled =
                              isSubmitting ||
                              alreadyAdded ||
                              (!isPositiveAdjustment &&
                                !hasAvailableStock);

                            return (
                              <article
                                key={`${variant.productId}-${variant.variantId}`}
                                className={styles.ProductResult}
                              >
                                {variant.productImage ? (
                                  <img
                                    src={variant.productImage}
                                    alt={variant.productName}
                                  />
                                ) : (
                                  <span
                                    className={
                                      styles.ProductImagePlaceholder
                                    }
                                    aria-hidden="true"
                                  >
                                    ▣
                                  </span>
                                )}

                                <div className={styles.ProductResultInfo}>
                                  <strong title={variant.productName}>
                                    {variant.productName}
                                  </strong>

                                  <span
                                    className={styles.ResultSku}
                                    title={`SKU: ${variant.sku || "Sin SKU"}`}
                                  >
                                    SKU: {variant.sku || "Sin SKU"}
                                  </span>

                                  <span
                                    className={styles.ResultVariant}
                                    title={variant.variantLabel}
                                  >
                                    {variant.variantLabel}
                                  </span>

                                  <span
                                    className={
                                      hasAvailableStock
                                        ? styles.ResultAvailable
                                        : styles.ResultOutOfStock
                                    }
                                  >
                                    Disponible:{" "}
                                    {formatNumber(
                                      variant.availableStock
                                    )}
                                  </span>
                                </div>

                                <button
                                  type="button"
                                  className={styles.AddProductButton}
                                  onClick={() => handleAddVariant(variant)}
                                  disabled={isDisabled}
                                >
                                  {!isPositiveAdjustment &&
                                  !hasAvailableStock
                                    ? "Sin stock"
                                    : alreadyAdded
                                    ? "Agregado"
                                    : "Agregar"}
                                </button>
                              </article>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </section>
                </div>

                <section className={styles.DetailCard}>
                  <header className={styles.SectionHeader}>
                    <div>
                      <h2>Detalle del ajuste</h2>

                      <p>
                        {isPositiveAdjustment
                          ? "Indica la cantidad y el costo unitario del incremento."
                          : "La cantidad no puede superar las existencias disponibles."}
                      </p>
                    </div>

                    <span className={styles.ItemsCounter}>
                      {totalItems}{" "}
                      {totalItems === 1 ? "producto" : "productos"}
                    </span>
                  </header>

                  {typeof errors.items === "string" && (
                    <div className={styles.DetailValidationMessage}>
                      {errors.items}
                    </div>
                  )}

                  {values.items.length === 0 ? (
                    <div className={styles.EmptyDetailState}>
                      <p>
                        Busca y agrega una variante para registrar el ajuste.
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className={styles.TableContainer}>
                        <table className={styles.Table}>
                          <thead>
                            <tr>
                              <th>Producto</th>
                              <th>SKU</th>
                              <th>Variante</th>
                              <th>Disponible</th>
                              <th>Cantidad</th>
                              {isPositiveAdjustment && (
                                <th>Costo unitario</th>
                              )}
                              {isPositiveAdjustment && <th>Total</th>}
                              <th aria-label="Acciones" />
                            </tr>
                          </thead>

                          <tbody>
                            {values.items.map((item, index) => {
                              const quantityError = getItemError(
                                errors,
                                index,
                                "quantity"
                              );

                              const unitCostError = getItemError(
                                errors,
                                index,
                                "unitCost"
                              );

                              const itemTotal =
                                (Number(item.quantity) || 0) *
                                (Number(item.unitCost) || 0);

                              return (
                                <tr key={item.localId}>
                                  <td>
                                    <div className={styles.ProductCell}>
                                      {item.productImage ? (
                                        <img
                                          src={item.productImage}
                                          alt={item.productName}
                                        />
                                      ) : (
                                        <span
                                          className={
                                            styles.ProductImagePlaceholder
                                          }
                                          aria-hidden="true"
                                        >
                                          ▣
                                        </span>
                                      )}

                                      <div>
                                        <strong title={item.productName}>
                                          {item.productName}
                                        </strong>

                                        <span
                                          title={
                                            item.productCode || "Sin código"
                                          }
                                        >
                                          {item.productCode ||
                                            "Sin código"}
                                        </span>
                                      </div>
                                    </div>
                                  </td>

                                  <td>
                                    <span
                                      className={styles.SkuCode}
                                      title={item.sku || "—"}
                                    >
                                      {item.sku || "—"}
                                    </span>
                                  </td>

                                  <td>
                                    <span
                                      className={styles.VariantLabel}
                                      title={item.variantLabel}
                                    >
                                      {item.variantLabel}
                                    </span>
                                  </td>

                                  <td>
                                    <span className={styles.AvailableStock}>
                                      {formatNumber(item.availableStock)}
                                    </span>
                                  </td>

                                  <td>
                                    <Field
                                      name={`items[${index}].quantity`}
                                      type="number"
                                      min="1"
                                      max={
                                        isPositiveAdjustment
                                          ? undefined
                                          : item.availableStock
                                      }
                                      step="1"
                                      className={
                                        quantityError
                                          ? styles.error
                                          : ""
                                      }
                                      disabled={isSubmitting}
                                    />

                                    {quantityError && (
                                      <span className={styles.FieldError}>
                                        {quantityError}
                                      </span>
                                    )}

                                    {!isPositiveAdjustment &&
                                      Number(item.quantity) >
                                        Number(item.availableStock) && (
                                        <span className={styles.FieldError}>
                                          No puede superar el disponible.
                                        </span>
                                      )}
                                  </td>

                                  {isPositiveAdjustment && (
                                    <td>
                                      <Field
                                        name={`items[${index}].unitCost`}
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        placeholder="0.00"
                                        className={
                                          unitCostError
                                            ? styles.error
                                            : ""
                                        }
                                        disabled={isSubmitting}
                                      />

                                      {unitCostError && (
                                        <span className={styles.FieldError}>
                                          {unitCostError}
                                        </span>
                                      )}
                                    </td>
                                  )}

                                  {isPositiveAdjustment && (
                                    <td>
                                      <strong className={styles.TotalValue}>
                                        {formatCurrency(itemTotal)}
                                      </strong>
                                    </td>
                                  )}

                                  <td>
                                    <button
                                      type="button"
                                      className={styles.RemoveItemButton}
                                      onClick={() =>
                                        handleRemoveItem(item.localId)
                                      }
                                      disabled={isSubmitting}
                                      aria-label={`Eliminar ${item.productName}`}
                                    >
                                      ×
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      <footer className={styles.DetailSummary}>
                        <div>
                          <span>Total ítems</span>
                          <strong>{totalItems}</strong>
                        </div>

                        <div>
                          <span>
                            {isPositiveAdjustment
                              ? "Total unidades a agregar"
                              : "Total unidades a reducir"}
                          </span>

                          <strong>{formatNumber(totalQuantity)}</strong>
                        </div>

                        <div
                          className={
                            isPositiveAdjustment
                              ? styles.PositiveSummary
                              : styles.NegativeSummary
                          }
                        >
                          <span>
                            {isPositiveAdjustment
                              ? "Valor del ajuste"
                              : "Validación"}
                          </span>

                          <strong>
                            {isPositiveAdjustment
                              ? formatCurrency(totalValue)
                              : "No se puede reducir más del disponible"}
                          </strong>
                        </div>
                      </footer>
                    </>
                  )}
                </section>
              </Form>
            </>
          );
        }}
      </Formik>
    </section>
  );
}